import axios from 'axios';
import { API_CONFIG } from 'src/config/api.config';

// Types for registration requests/responses
export interface RuRegistrationRequest {
  // Company details
  companyName: string;
  vatNumber: string;
  address: string;
  city: string;
  zipCode: string;
  country: string;

  // Contact information
  contactEmail: string;
  contactPhone?: string;

  // Initial admin user details
  adminName: string;
  adminEmail: string;
  adminPassword: string;

  // Additional information
  additionalInfo?: string;
}

export interface RuRegistrationResponse {
  requestId: string;
  companyName: string;
  message: string;
  status: string;
  requestDate: string;
}

// Railway Undertaking Summary DTO
export interface RuSummaryDto {
  id: string;
  name: string;
  vatNumber: string;
  contactEmail: string;
  adminName: string;
  adminEmail: string;
  country: string;
  createdAt: string;
}

// Define API error type for proper error handling
interface ApiError {
  response?: {
    status?: number;
    data?: {
      message?: string;
    };
    headers?: Record<string, string>;
  };
  request?: unknown;
  message?: string;
}

/**
 * Service for handling registration-related API calls
 */
export const registrationService = {
  /**
   * Submit a new Railway Undertaking registration
   *
   * @param registrationData The registration data to submit
   * @returns Promise with the registration response
   */
  submitRuRegistration: async (
    registrationData: RuRegistrationRequest
  ): Promise<RuRegistrationResponse> => {
    try {
      const url = API_CONFIG.getFullUrl(API_CONFIG.endpoints.registration.ru);

      // Log request for debugging
      // console.log('Submitting RU registration:', registrationData);

      // Ensure the data structure matches what the backend expects
      const apiRequestData = {
        ...registrationData
      };

      const response = await axios.post<RuRegistrationResponse>(
        url,
        apiRequestData,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      return response.data;
    } catch (error: unknown) {
      // Cast to ApiError to access properties in a type-safe way
      const apiError = error as ApiError;
      // console.error('Error submitting RU registration:', error);

      // Provide more details about the error
      if (apiError.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        if (apiError.response.data) {
          console.error('Response data:', apiError.response.data);
        }
        if (apiError.response.status) {
          console.error('Response status:', apiError.response.status);
        }
        if (apiError.response.headers) {
          console.error('Response headers:', apiError.response.headers);
        }
      } else if (apiError.request) {
        // The request was made but no response was received
        console.error('No response received:', apiError.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Error setting up request:', apiError.message);
      }

      throw error;
    }
  },

  /**
   * Get a list of pending Railway Undertaking registrations
   *
   * @returns A promise that resolves with the list of pending RU summary DTOs
   */
  getPendingRuRegistrations: async (): Promise<RuSummaryDto[]> => {
    try {
      // console.log('Fetching pending RU registrations...');
      const url = API_CONFIG.getFullUrl(API_CONFIG.endpoints.registration.ru);

      const response = await axios.get<RuSummaryDto[]>(url);

      return response.data;
    } catch (error: unknown) {
      // Cast to ApiError to access properties in a type-safe way
      const apiError = error as ApiError;

      // Provide more details about the error
      if (apiError.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        if (apiError.response.data) {
          console.error('Response data:', apiError.response.data);
        }
        if (apiError.response.status) {
          console.error('Response status:', apiError.response.status);
        }
        if (apiError.response.headers) {
          console.error('Response headers:', apiError.response.headers);
        }
      } else if (apiError.request) {
        // The request was made but no response was received
        console.error('No response received:', apiError.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Error setting up request:', apiError.message);
      }

      throw error;
    }
  }
};

export default registrationService;
